/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit'

import { get } from 'utils/lodash'

const initialState = {
  query: '',
  activeMatchIndex: 0,
  // Matched row ids per grid, not just a count — see selectIsRowMatchForGrid.
  matchRowIdsByGridId: {},
  activeMatchRowIdByGridId: {},
  // The search dropdown's rows, from Board V2 or the List; a row's index is its activeMatchIndex. Empty on the legacy board.
  results: [],
  isSearching: false,
  // 'truncated' (server capped at 50) or 'incomplete' (searched only what has loaded); the dropdown says which.
  notice: null,
  // The dropdown result last clicked, numbered so picking the same one twice still registers; the List opens its details.
  pickedResult: null,
  pickCount: 0,
}

const masterRouteSearchSlice = createSlice({
  name: 'masterRouteSearch',
  initialState,
  reducers: {
    setSearchQuery: (state, action) => {
      state.query = action.payload
      state.activeMatchIndex = 0
      if (!action.payload) {
        state.matchRowIdsByGridId = {}
        state.activeMatchRowIdByGridId = {}
        state.results = []
      }
    },
    incrementActiveMatch: (state, action) => {
      const counts = Object.values(state.matchRowIdsByGridId).map(lookup => Object.keys(lookup).length)
      const total = counts.length === 0 ? 0 : Math.max(...counts)
      if (total === 0) return
      const delta = action.payload
      state.activeMatchIndex = (state.activeMatchIndex + delta + total) % total
    },
    // Jump directly to a match by its index in `results` — used by the results
    // dropdown when a row is clicked (the row's index is the match index).
    setActiveMatchIndex: (state, action) => {
      state.activeMatchIndex = action.payload
    },
    setSearchResults: (state, action) => {
      state.results = action.payload
    },
    setGridMatches: (state, action) => {
      const { gridId, rowIds } = action.payload
      if (!rowIds || rowIds.length === 0) {
        delete state.matchRowIdsByGridId[gridId]
      } else {
        const lookup = {}
        rowIds.forEach(id => {
          lookup[id] = true
        })
        state.matchRowIdsByGridId[gridId] = lookup
      }
    },
    setActiveMatchRowId: (state, action) => {
      const { gridId, rowId } = action.payload
      if (!rowId) {
        delete state.activeMatchRowIdByGridId[gridId]
      } else {
        state.activeMatchRowIdByGridId[gridId] = rowId
      }
    },
    setIsSearching: (state, action) => {
      state.isSearching = Boolean(action.payload)
    },
    setSearchNotice: (state, action) => {
      state.notice = action.payload ?? null
    },
    pickSearchResult: (state, action) => {
      state.pickedResult = action.payload
      state.pickCount += 1
    },
    resetSearch: () => initialState,
  },
})

export const {
  setSearchQuery,
  incrementActiveMatch,
  setActiveMatchIndex,
  setSearchResults,
  setGridMatches,
  setActiveMatchRowId,
  setIsSearching,
  setSearchNotice,
  pickSearchResult,
  resetSearch,
} = masterRouteSearchSlice.actions

export const selectSearchQuery = s => get(s, 'masterRouteSearch.query', '')
export const selectActiveMatchIndex = s => get(s, 'masterRouteSearch.activeMatchIndex', 0)
export const selectSearchResults = s => get(s, 'masterRouteSearch.results', [])
export const selectMaxMatchCount = s => {
  const counts = Object.values(get(s, 'masterRouteSearch.matchRowIdsByGridId', {})).map(lookup => Object.keys(lookup).length)
  return counts.length === 0 ? 0 : Math.max(...counts)
}
export const selectActiveMatchRowIdForGrid = gridId => s => get(s, ['masterRouteSearch', 'activeMatchRowIdByGridId', gridId], null)
// Per-row booleans so a card subscribes only to its own match/active state, not to every query change.
export const selectIsRowMatchForGrid = (gridId, rowId) => s =>
  rowId != null && Boolean(get(s, ['masterRouteSearch', 'matchRowIdsByGridId', gridId], {})[rowId])
export const selectIsActiveMatchForGrid = (gridId, rowId) => s =>
  rowId != null && get(s, ['masterRouteSearch', 'activeMatchRowIdByGridId', gridId], null) === rowId
// Board V2 routes all matching through one synthetic grid ('boardV2') since the
// match list is built centrally from loaded stops, not per-column grids.
export const BOARD_V2_SEARCH_GRID_ID = 'boardV2'
export const LIST_SEARCH_GRID_ID = 'list'
export const selectActiveMatchRowId = s => get(s, ['masterRouteSearch', 'activeMatchRowIdByGridId', BOARD_V2_SEARCH_GRID_ID], null)
export const selectIsSearching = s => get(s, 'masterRouteSearch.isSearching', false)
export const selectActiveSearchResult = s => {
  const results = get(s, 'masterRouteSearch.results', [])
  return results[Math.min(get(s, 'masterRouteSearch.activeMatchIndex', 0), results.length - 1)] ?? null
}
export const SEARCH_NOTICE_TRUNCATED = 'truncated'
export const SEARCH_NOTICE_INCOMPLETE = 'incomplete'
export const selectSearchNotice = s => get(s, 'masterRouteSearch.notice', null)
export const selectPickedSearchResult = s => get(s, 'masterRouteSearch.pickedResult', null)
export const selectSearchPickCount = s => get(s, 'masterRouteSearch.pickCount', 0)

export default masterRouteSearchSlice.reducer
